import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, Tag, ArrowLeft } from 'lucide-react';
import type { BlogConfig, BlogPost, PostNavigation, BlogThemeStyles } from './types';
import { getCroppedImageRenderProps } from '../../components/ComponentEditModal/shared/cropUtils';
import type { CropData } from '../../components/ComponentEditModal/shared/ImageCropModal';

interface FontStyles {
  headingFont: React.CSSProperties;
  bodyFont: React.CSSProperties;
}

interface BlogPostViewProps {
  blog: BlogConfig;
  post: BlogPost;
  navigation: PostNavigation | null;
  theme: BlogThemeStyles;
  fontStyles?: FontStyles;
}

export const BlogPostView: React.FC<BlogPostViewProps> = ({
  blog,
  post,
  navigation,
  theme,
  fontStyles,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen" style={{ background: theme.pageBackground }}>
      {/* Header */}
      <header
        style={{
          backgroundColor: theme.headerBackground,
          borderBottom: `1px solid ${theme.cardBorder}`,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link
            to={blog.basePath}
            className="inline-flex items-center transition-colors"
            style={{ color: theme.linkColor }}
            onMouseOver={e => (e.currentTarget.style.color = theme.linkHoverColor || '')}
            onMouseOut={e => (e.currentTarget.style.color = theme.linkColor || '')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {blog.title}
          </Link>
        </div>
      </header>

      {/* Post content */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Featured image */}
        {blog.showFeaturedImage &&
          post.featuredImageUrl &&
          (() => {
            const { hasCrop, aspectRatio, imageStyle } = getCroppedImageRenderProps(
              post.featuredImageCrop as CropData | undefined
            );

            if (hasCrop) {
              return (
                <div
                  className="relative w-full overflow-hidden rounded-lg mb-8"
                  style={{ aspectRatio, width: '100%' }}
                >
                  <img src={post.featuredImageUrl} alt={post.title} style={imageStyle} />
                </div>
              );
            }

            return (
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full rounded-lg mb-8 object-cover"
                style={{ maxHeight: '32rem' }}
              />
            );
          })()}

        {/* Post header */}
        <header className="mb-8">
          <h1
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: theme.postTitleColor, ...fontStyles?.headingFont }}
          >
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4" style={{ color: theme.postMetaColor }}>
            {blog.showAuthor && post.authorName && (
              <span className="flex items-center">
                <User className="w-4 h-4 mr-1" style={{ color: theme.iconColor }} />
                {post.authorName}
              </span>
            )}
            {blog.showDate && (
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" style={{ color: theme.iconColor }} />
                {formatDate(post.publishedAt || post.createdAt)}
              </span>
            )}
            {blog.showReadingTime && post.readingTimeMinutes && (
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" style={{ color: theme.iconColor }} />
                {post.readingTimeMinutes} min read
              </span>
            )}
          </div>

          {/* Tags */}
          {blog.showTags && post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {post.tags.map(t => (
                <Link
                  key={t}
                  to={`${blog.basePath}/tag/${t}`}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors"
                  style={{
                    backgroundColor: theme.tagBackground,
                    color: theme.tagText,
                  }}
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {t}
                </Link>
              ))}
            </div>
          )}
        </header>

        {/* Post content with theme-aware prose styles */}
        <style>
          {`
            .blog-prose-themed {
              color: ${theme.postTextColor} !important;
              ${fontStyles?.bodyFont?.fontFamily ? `font-family: ${fontStyles.bodyFont.fontFamily} !important;` : ''}
            }
            .blog-prose-themed p,
            .blog-prose-themed li,
            .blog-prose-themed span {
              color: ${theme.postTextColor} !important;
            }
            .blog-prose-themed h1,
            .blog-prose-themed h2,
            .blog-prose-themed h3,
            .blog-prose-themed h4,
            .blog-prose-themed h5,
            .blog-prose-themed h6 {
              color: ${theme.postTitleColor} !important;
              ${fontStyles?.headingFont?.fontFamily ? `font-family: ${fontStyles.headingFont.fontFamily} !important;` : ''}
            }
            .blog-prose-themed strong {
              color: ${theme.postTitleColor} !important;
            }
            .blog-prose-themed a {
              color: ${theme.linkColor} !important;
            }
            .blog-prose-themed a:hover {
              color: ${theme.linkHoverColor} !important;
            }
            .blog-prose-themed blockquote {
              color: ${theme.postTextColor} !important;
              border-left-color: ${theme.cardBorder} !important;
            }
            .blog-prose-themed code {
              color: ${theme.postTitleColor} !important;
            }
            .blog-prose-themed pre {
              background-color: ${theme.cardBackground} !important;
              color: ${theme.postTextColor} !important;
            }
            .blog-prose-themed hr {
              border-color: ${theme.cardBorder} !important;
            }
            .blog-prose-themed ul > li::marker,
            .blog-prose-themed ol > li::marker {
              color: ${theme.mutedTextColor} !important;
            }
            .blog-prose-themed table {
              border-collapse: collapse;
              width: 100%;
            }
            .blog-prose-themed th,
            .blog-prose-themed td {
              border: 1px solid ${theme.cardBorder} !important;
              padding: 0.5rem 0.75rem;
            }
            .blog-prose-themed th {
              background-color: ${theme.sidebarBackground} !important;
              color: ${theme.postTitleColor} !important;
              font-weight: 600;
            }
            .blog-prose-themed td {
              background-color: ${theme.cardBackground} !important;
              color: ${theme.postTextColor} !important;
            }
            .blog-prose-themed img {
              border-radius: 0.5rem;
            }
            .blog-prose-themed iframe {
              border-radius: 0.5rem;
              max-width: 100%;
            }
            .blog-prose-themed mark {
              background-color: rgba(254, 240, 138, 0.5);
              color: inherit;
            }
          `}
        </style>
        <div
          className="prose prose-lg max-w-none blog-prose-themed"
          dangerouslySetInnerHTML={{ __html: post.content || '' }}
        />

        {/* Previous/Next Navigation */}
        {navigation && (navigation.previous || navigation.next) && (
          <nav className="mt-12 pt-8 border-t" style={{ borderColor: theme.cardBorder }}>
            <div className="flex justify-between items-stretch gap-4">
              {/* Previous Post (Newer) */}
              <div className="flex-1">
                {navigation.previous ? (
                  <Link
                    to={`${blog.basePath}/${navigation.previous.slug}`}
                    className="group block p-4 rounded-lg border transition-colors h-full"
                    style={{
                      borderColor: theme.cardBorder,
                      backgroundColor: theme.cardBackground,
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.borderColor = theme.linkColor || '';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.borderColor = theme.cardBorder || '';
                    }}
                  >
                    <span className="text-sm font-medium" style={{ color: theme.mutedTextColor }}>
                      ← Previous
                    </span>
                    <p
                      className="mt-1 font-medium line-clamp-2"
                      style={{ color: theme.linkColor, ...fontStyles?.headingFont }}
                    >
                      {navigation.previous.title}
                    </p>
                  </Link>
                ) : (
                  <div />
                )}
              </div>

              {/* Next Post (Older) */}
              <div className="flex-1">
                {navigation.next ? (
                  <Link
                    to={`${blog.basePath}/${navigation.next.slug}`}
                    className="group block p-4 rounded-lg border transition-colors text-right h-full"
                    style={{
                      borderColor: theme.cardBorder,
                      backgroundColor: theme.cardBackground,
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.borderColor = theme.linkColor || '';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.borderColor = theme.cardBorder || '';
                    }}
                  >
                    <span className="text-sm font-medium" style={{ color: theme.mutedTextColor }}>
                      Next →
                    </span>
                    <p
                      className="mt-1 font-medium line-clamp-2"
                      style={{ color: theme.linkColor, ...fontStyles?.headingFont }}
                    >
                      {navigation.next.title}
                    </p>
                  </Link>
                ) : (
                  <div />
                )}
              </div>
            </div>
          </nav>
        )}
      </article>
    </div>
  );
};
